import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { googleChatApi } from '@/api/google-chat';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { GoogleChatConfigPatch, GoogleChatConfigView } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

export function GoogleChatTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const query = useQuery({
    queryKey: ['org', 'google-chat', currentBotId],
    queryFn: () => googleChatApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: GoogleChatConfigPatch) => googleChatApi.update(currentBotId!, patch),
    onSuccess: () => {
      toast.success(
        t('organization.googleChat.savedToast', { defaultValue: 'Google Chat configuration saved' }),
      );
      void queryClient.invalidateQueries({ queryKey: ['org', 'google-chat', currentBotId] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t('organization.googleChat.selectBot', {
            defaultValue: 'Select a bot to configure its Google Chat channel.',
          })}
        </CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  if (query.error || !query.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">{unwrapError(query.error)}</CardContent>
      </Card>
    );
  }

  const config = query.data;

  return (
    <div className="space-y-6">
      <ActivationCard
        config={config}
        canManage={canManage}
        onToggle={(enabled) => updateMutation.mutate({ enabled })}
        pending={updateMutation.isPending}
      />
      <SpaceIdsCard
        config={config}
        canManage={canManage}
        onSave={(spaceIds) => updateMutation.mutate({ spaceIds })}
        pending={updateMutation.isPending}
      />
    </div>
  );
}

function ActivationCard({
  config,
  canManage,
  onToggle,
  pending,
}: {
  config: GoogleChatConfigView;
  canManage: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('organization.googleChat.activation.title', { defaultValue: 'Activation' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('organization.googleChat.activation.subtitle', {
              defaultValue:
                'Reply when @mentioned or DM\'d in the linked internal Google Chat spaces below. Internal Redspher use only — no passive listening.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('organization.googleChat.activation.on', { defaultValue: 'Enabled' })
              : t('organization.googleChat.activation.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch checked={config.enabled} disabled={!canManage || pending} onCheckedChange={onToggle} />
        </div>
      </CardHeader>
    </Card>
  );
}

function SpaceIdsCard({
  config,
  canManage,
  onSave,
  pending,
}: {
  config: GoogleChatConfigView;
  canManage: boolean;
  onSave: (spaceIds: string[]) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [entries, setEntries] = React.useState<string[]>(config.spaceIds);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    setEntries(config.spaceIds);
  }, [config.spaceIds]);

  const dirty = JSON.stringify(entries) !== JSON.stringify(config.spaceIds);

  const addEntry = (): void => {
    const next = draft.trim();
    if (!next) return;
    if (entries.includes(next)) {
      toast.error(t('organization.googleChat.spaceIds.duplicate', { defaultValue: 'Space already in the list' }));
      return;
    }
    setEntries([...entries, next]);
    setDraft('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('organization.googleChat.spaceIds.title', { defaultValue: 'Linked spaces' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.googleChat.spaceIds.subtitle', {
            defaultValue:
              'Google Chat space resource names (e.g. spaces/AAAAAAAAAAA) this bot answers in. A space can only be linked to one bot at a time.',
          })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="spaces/AAAAAAAAAAA"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEntry();
              }
            }}
            disabled={!canManage}
          />
          <Button onClick={addEntry} disabled={!canManage || !draft.trim()} className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t('common.add', { defaultValue: 'Add' })}
          </Button>
        </div>
        {entries.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            {t('organization.googleChat.spaceIds.empty', {
              defaultValue: 'No space linked yet — the bot will not answer anywhere on Google Chat until you add one.',
            })}
          </div>
        ) : (
          <ul className="space-y-1">
            {entries.map((entry) => (
              <li
                key={entry}
                className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 font-mono text-xs"
              >
                <span>{entry}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  disabled={!canManage}
                  onClick={() => setEntries(entries.filter((e) => e !== entry))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setEntries(config.spaceIds)} disabled={!dirty || pending}>
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
          <Button onClick={() => onSave(entries)} disabled={!canManage || !dirty || pending} className="gap-1.5">
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
